import {
    FilterFunctionParams,
    OptionsData,
    SDeselectItem,
    SSelectCustomEvent,
    SSelectItem,
} from "@avalara/skylab-sdk/loader";
import { SCol, SRow, SSelect } from "@avalara/skylab-sdk/react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { assignBucketToUser } from "../../../../app/bucketsSlice/bucketActions";
import { AppDispatch } from "../../../../app/types";
import featureFlag from "../../../../featureToggler/featureFlag";
import { useIsAuthorized } from "../../../../hooks/useIsAuthorized";
import type { AssignBucketToUserPayload, AssignUserProps, User } from "./types";

const AssignUser = ({ availableUsers, isUserLoading, bucketId }: AssignUserProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<string[]>([]);
    const [isAssigning, setIsAssigning] = useState<boolean>(false);
    const canAddEditDeleteBucketsUsers = useIsAuthorized(
        featureFlag.settings.addEditDeleteBucketsUsers
    );
    const getUserOptions = useCallback((): OptionsData[] => {
        return (availableUsers || []).map((user: User) => ({
            label: user.userName,
            value: String(user.id),
            selected: selectedUsersToAssign.includes(String(user.id)),
        }));
    }, [availableUsers, selectedUsersToAssign]);

    const handleUserSelect = useCallback(
        (e: SSelectCustomEvent<SSelectItem>): void => {
            const newValue = e?.detail?.item?.value;
            if (newValue) {
                const currentValues = selectedUsersToAssign || [];
                if (!currentValues.includes(newValue)) {
                    setSelectedUsersToAssign([...currentValues, newValue]);
                }
            }
        },
        [selectedUsersToAssign]
    );

    const handleUserDeselect = useCallback(
        (e: SSelectCustomEvent<SDeselectItem>): void => {
            if (e?.detail?.isDeselectAll) {
                setSelectedUsersToAssign([]);
                return;
            }
            const deselectedValue = e?.detail?.item?.value;
            if (deselectedValue) {
                const currentValues = selectedUsersToAssign.filter(
                    (v: string) => v !== deselectedValue
                );
                setSelectedUsersToAssign(currentValues);
            }
        },
        [selectedUsersToAssign]
    );

    const handleAssignUser = useCallback((): void => {
        if (selectedUsersToAssign.length > 0) {
            setIsAssigning(true);
            const payload: AssignBucketToUserPayload = {
                UserIds: selectedUsersToAssign.map(Number),
            };
            dispatch(assignBucketToUser(payload, bucketId));
            setSelectedUsersToAssign([]);
            setIsAssigning(false);
        }
    }, [selectedUsersToAssign, dispatch, bucketId]);

    const filterFunction = useCallback((params: FilterFunctionParams): boolean => {
        return params.item.label.toLowerCase().includes(params.query.toLowerCase());
    }, []);

    return (
        <SRow className="margin-bottom-md">
            <SCol span="12">
                <h3>Assign users to the bucket</h3>
                <div className="margin-bottom-md">
                    <SRow className="align-items-center">
                        <SCol span="6">
                            <label htmlFor="user" id="lbl-user">
                                Search by name or email address
                            </label>
                            <SSelect
                                inputId="user"
                                multiple
                                predictiveSearch
                                optionsList={getUserOptions()}
                                onS-select={handleUserSelect}
                                onS-deselect={handleUserDeselect}
                                filterFunction={filterFunction}
                                disabled={isUserLoading}>
                                <div slot="sticky-footer">
                                    <button
                                        className="primary margin-all-sm"
                                        onClick={handleAssignUser}
                                        disabled={
                                            isAssigning ||
                                            selectedUsersToAssign.length === 0 ||
                                            !canAddEditDeleteBucketsUsers
                                        }>
                                        Assign
                                    </button>
                                </div>
                            </SSelect>
                        </SCol>
                    </SRow>
                </div>
            </SCol>
        </SRow>
    );
};

AssignUser.displayName = "AssignUser";
export default AssignUser;
